/**
 * Geld anzeigen — mehr nicht.
 *
 * **Hier wird nicht gerechnet.** Gerundet wird ausschließlich in
 * `shared/money.py`; Salden, Steuer und anteilige Beträge kommen fertig vom
 * Server (Invariante I9). Sonst existiert jede Regel dreifach — Python,
 * TypeScript, Swift — und die drei driften auseinander.
 *
 * Der Regelfall ist deshalb, `amount_display` vom Server zu zeigen. Die
 * Funktion hier ist für die Fälle, in denen es das nicht gibt: eine Summe, die
 * der Server als reine Zahl liefert, oder ein Eingabefeld.
 */

const FORMAT = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/**
 * Cent als Betrag mit Tausendertrennzeichen: `123456` → `1.234,56 €`.
 *
 * Die Division durch 100 ist die einzige erlaubte Rechnung mit Geld im Client,
 * und sie ist keine Fachlogik, sondern Darstellung: Cent sind die Einheit, Euro
 * die Anzeige. Alles darüber hinaus — addieren, prozentuieren, aufteilen —
 * gehört ins Backend.
 */
export function formatCents(cents: number): string {
  return FORMAT.format(cents / 100)
}

/** `-1234,567` → Vorzeichen, Vorkommateil, Nachkommateil. */
const DECIMAL = /^(-?)(\d*)(?:\.(\d*))?$/

/**
 * Eingabe wie `1.234,56` oder `1234,56` in Cent.
 *
 * Rundet kaufmännisch auf ganze Cent, weil ein Eingabefeld mit
 * `inputMode="decimal"` mehr Nachkommastellen zulässt, als es Cent gibt.
 * Liefert `null` bei allem, was keine Zahl ist — die Meldung formuliert das
 * Formular, nicht diese Funktion.
 *
 * **Ohne Gleitkomma.** `Math.round(Number("1.005") * 100)` ergibt 100, nicht
 * 101: Der Zwischenwert ist 100.49999999999999, weil 1,005 in einem `double`
 * nicht darstellbar ist. Der Server rundet mit `Decimal` kaufmännisch
 * (`shared/money.py::round_half_up`) und käme auf 101 — der Client entschiede
 * also anders als die eine Rundung des Projekts (I1).
 *
 * Deshalb wird hier ausschließlich mit ganzen Zahlen gerechnet: Vorkommateil
 * mal 100, plus die ersten zwei Nachkommastellen, plus eins, wenn die dritte
 * mindestens 5 ist. Alle Summanden sind exakt darstellbar. Bei negativen
 * Beträgen wird vom Nullpunkt weg gerundet, wie `ROUND_HALF_UP` es tut.
 */
export function parseCents(input: string): number | null {
  const normalized = input.trim().replaceAll('.', '').replace(',', '.')
  if (normalized === '') return null

  const match = DECIMAL.exec(normalized)
  if (match === null) return null

  const [, sign, whole = '', fraction = ''] = match
  if (whole === '' && fraction === '') return null

  const cents = Number(whole || '0') * 100 + Number(`${fraction}00`.slice(0, 2))
  const rounded = fraction.length > 2 && Number(fraction[2]) >= 5 ? cents + 1 : cents
  if (!Number.isSafeInteger(rounded)) return null

  return sign === '-' ? -rounded : rounded
}

/**
 * Cent als bearbeitbare Zahl ohne Währungszeichen: `123456` → `1234,56`.
 *
 * Für das Vorbelegen eines Eingabefelds beim Bearbeiten. `formatCents`
 * eignet sich dafür nicht: Sein Ergebnis („1.234,56 €") trägt Tausenderpunkt
 * und Währungszeichen, und `parseCents` liest genau das nicht zurück, weil
 * der Punkt mit dem eigenen Tausendertrennzeichen kollidiert.
 */
export function centsToDecimalInput(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',')
}

/**
 * Der im Zahlungssheet verbleibende Betrag. Zuordnungen und Vormerkungen sind
 * bereits servergelieferte ganze Centbeträge; diese Rechnung rundet nichts.
 */
export function remainingCents(amountCents: number | null, allocatedCents: number, reservedCents: number): number | null {
  return amountCents === null ? null : amountCents - allocatedCents - reservedCents
}
